import { SearchInputProps, SelectDropdownInputOption } from '@va/ui/design-system';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { RadioInputMultiSelectSectionList } from '../../inputs/radio-input-multi-select-section';
import { AccordionSection, AccordionSectionProps } from '../accordion-section/AccordionSection';

type AccordionMultiSelectProps = AccordionSectionProps & {
  options: SelectDropdownInputOption[];
  selectedOptions?: SelectDropdownInputOption[];
  onChange: (options: SelectDropdownInputOption[]) => void;
  searchInputProps?: SearchInputProps;
  isLoading?: boolean;
  showSubtitle?: boolean;
  useCustomTitle?: boolean;
  useCustomSubtitle?: boolean;
  optional?: boolean;
  closeOnChange?: boolean;
  disabled?: boolean;
  disabledTooltip?: ReactNode;
  listClassName?: string;
  hint?: string;
  floating?: boolean;
  shouldCloseOnOutsideClick?: boolean;
  onClose?: () => void;
};

export const AccordionMultiSelect: React.FC<AccordionMultiSelectProps> = ({
  required,
  icon,
  bgColorClass,
  title,
  subtitle,
  options,
  selectedOptions,
  onChange,
  searchInputProps,
  isLoading,
  titleTooltip,
  titleTooltipProps,
  showSubtitle = true,
  'data-testid': dataTestId,
  useCustomTitle,
  useCustomSubtitle,
  titleClassName,
  subtitleClassName,
  optional,
  closeOnChange = true,
  disabled = false,
  disabledTooltip,
  wrapperClassName,
  listClassName,
  floating = false,
  shouldCloseOnOutsideClick = false,
  size,
  hint,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const sectionSubtitle = useMemo(() => {
    if (!showSubtitle) return;
    return useCustomSubtitle && subtitle
      ? subtitle
      : (selectedOptions?.map((option) => option.value).join(', ') ?? subtitle);
  }, [showSubtitle, useCustomSubtitle, subtitle, selectedOptions]);

  const sectionTitle = useMemo(() => {
    if (useCustomTitle && title) return title;

    const generatedLabel = selectedOptions?.flatMap((option) => option.label || []).join(', ');

    return generatedLabel?.length ? generatedLabel : title;
  }, [useCustomTitle, title, selectedOptions]);

  useEffect(() => {
    if (!isOpen) onClose?.();
  }, [isOpen, onClose]);

  return (
    <AccordionSection
      required={required}
      optional={optional}
      icon={icon}
      titleTooltipProps={titleTooltipProps}
      titleTooltip={selectedOptions ? undefined : titleTooltip}
      bgColorClass={bgColorClass}
      title={sectionTitle}
      subtitle={sectionSubtitle}
      isOpenExternal={isOpen}
      toggleIsOpenExternal={() => {
        setIsOpen((prev) => !prev);
      }}
      data-testid={dataTestId}
      titleClassName={titleClassName}
      subtitleClassName={subtitleClassName}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      wrapperClassName={wrapperClassName}
      floating={floating}
      shouldCloseOnOutsideClick={shouldCloseOnOutsideClick}
      size={size}
    >
      <RadioInputMultiSelectSectionList
        isLoading={isLoading}
        searchInputProps={searchInputProps}
        options={options}
        selectedOptions={selectedOptions}
        onChange={(options) => {
          onChange(options);
          if (closeOnChange) {
            setIsOpen(false);
          }
        }}
        listClassName={listClassName}
        floating={floating}
        hint={hint}
      />
    </AccordionSection>
  );
};
